import { useCallback, useEffect, useRef, useState } from "react";

import useApiMethods from "../lib/useApiMethods";
import ToastMsg from "../components/common/ToastMsg";
import {
    AI_CHAT,
    AI_CHAT_HISTORY,
    AI_FEEDBACK,
} from "../constants/api";

/**
 * State + backend calls for the AI Copilot.
 *
 * One instance owns one conversation. The docked launcher and the full-page
 * workspace each mount their own instance but share a session id through
 * sessionStorage, so a conversation started in the widget continues on the page.
 *
 * Backend contract (BE/ai):
 *   POST ai/chat/          { query, session_id, history_limit } -> { status, data }
 *   GET  ai/chat/                                              -> { status, data: [sessions] }
 *   GET  ai/chat/history/  ?session_id=                        -> { status, data: {session, messages} }
 *   POST ai/feedback/      { message_id, rating, feedback_text }
 */

const SESSION_STORAGE_KEY = "copilot_session_id";

const newSessionId = () =>
    `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const readStoredSessionId = () => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEY) || null;
    } catch (_) {
        return null;
    }
};

const writeStoredSessionId = (value) => {
    try {
        if (value) sessionStorage.setItem(SESSION_STORAGE_KEY, value);
        else sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {
        /* storage unavailable (private mode) — the id still lives in state */
    }
};

const nowLabel = () =>
    new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

/** Map one persisted history row onto the shape the thread renders. */
const fromHistoryRow = (row) => ({
    id: row.id,
    sender: row.sender,
    text: row.sender === "user" ? row.query : row.response,
    domain: row.domain,
    intent: row.intent,
    data: row.sender === "user" ? null : row.data,
    isLlm: row.is_llm_generated,
    model: row.model_used,
    rating: row.rating,
    timestamp: row.created_date
        ? new Date(row.created_date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
        : "",
});

const useCopilot = () => {
    const { fetch, create, remove } = useApiMethods();

    const [sessionId, setSessionId] = useState(() => readStoredSessionId() || newSessionId());
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [feedbackState, setFeedbackState] = useState({});

    // Guards against a late response from a previous session overwriting the
    // thread after the user has switched or started a new chat.
    const activeSessionRef = useRef(sessionId);
    useEffect(() => {
        activeSessionRef.current = sessionId;
        writeStoredSessionId(sessionId);
    }, [sessionId]);

    /** Ask the copilot a question with optional document attachment. */
    const send = useCallback(
        async (rawQuery, attachment = null) => {
            const query = (rawQuery || "").trim();
            if ((!query && !attachment) || sending) return;

            const targetSession = activeSessionRef.current;

            setMessages((prev) => [
                ...prev,
                {
                    id: `local-${Date.now()}`,
                    sender: "user",
                    text: query || (attachment ? `Uploaded document: ${attachment.name}` : ""),
                    attachment: attachment ? { name: attachment.name, size: attachment.size } : null,
                    timestamp: nowLabel(),
                },
            ]);
            setSending(true);

            try {
                const res = await create(AI_CHAT, {
                    query,
                    attachment_name: attachment?.name || "",
                    attachment_content: attachment?.content || "",
                    session_id: targetSession,
                    history_limit: 4,
                });

                // Session switched while the request was in flight — drop the answer.
                if (activeSessionRef.current !== targetSession) return;

                if (res?.status !== "success" || !res?.data) {
                    ToastMsg("error", res?.message || "The copilot could not answer just now.");
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `error-${Date.now()}`,
                            sender: "ai",
                            text:
                                res?.message ||
                                "I could not reach the HR assistant. Please try again in a moment.",
                            domain: "general",
                            isError: true,
                            timestamp: nowLabel(),
                        },
                    ]);
                    return;
                }

                const payload = res.data;

                // The backend generates a session id when the client sends none,
                // and disambiguates a clashing one — always adopt what it returns.
                if (payload.session_id && payload.session_id !== targetSession) {
                    setSessionId(payload.session_id);
                }

                setMessages((prev) => [
                    ...prev,
                    {
                        id: payload.message_id,
                        sender: "ai",
                        text: payload.response,
                        domain: payload.domain,
                        intent: payload.intent,
                        data: payload.data,
                        isLlm: payload.is_llm_generated,
                        model: payload.model_used,
                        // 'denied' means an org-wide question was scoped down to the
                        // caller's own data — worth surfacing, not an error.
                        isRestricted: payload.result_status === "denied",
                        timestamp: nowLabel(),
                    },
                ]);
            } finally {
                setSending(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sending]
    );

    /** Rate one answer. Optimistic — the tick shows before the POST resolves. */
    const rate = useCallback(
        async (messageId, direction) => {
            if (!messageId || String(messageId).startsWith("local-")) return;
            setFeedbackState((prev) => ({ ...prev, [messageId]: direction }));

            const res = await create(AI_FEEDBACK, {
                message_id: messageId,
                rating: direction === "up" ? 5 : 1,
            });

            if (res?.status !== "success") {
                setFeedbackState((prev) => {
                    const next = { ...prev };
                    delete next[messageId];
                    return next;
                });
                ToastMsg("error", res?.message || "Could not record that feedback.");
                return;
            }
            ToastMsg("success", "Thanks for the feedback");
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /** The caller's recent sessions, newest first. */
    const loadSessions = useCallback(
        async (signal) => {
            const res = await fetch(AI_CHAT, {}, setLoadingSessions, signal);
            if (res?.status === "cancelled") return;
            setSessions(Array.isArray(res?.data) ? res.data : []);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /**
     * Replay a stored session into the thread.
     *
     * `silent` suppresses the error toast, for speculative restores where the
     * session may legitimately not exist yet (a fresh id that has never been
     * sent to the backend returns 400 from the history endpoint).
     */
    const openSession = useCallback(
        async (id, { silent = false } = {}) => {
            if (!id) return;
            setSessionId(id);
            activeSessionRef.current = id;
            setMessages([]);

            const res = await fetch(AI_CHAT_HISTORY, { session_id: id }, setLoadingHistory);
            if (res?.status === "cancelled") return;
            if (res?.status !== "success") {
                if (!silent) ToastMsg("error", res?.message || "Could not load that conversation.");
                return;
            }

            // Ignore if the user moved on while this was loading.
            if (activeSessionRef.current !== id) return;

            const rows = Array.isArray(res.data?.messages) ? res.data.messages : [];
            setMessages(rows.map(fromHistoryRow));
            setFeedbackState(
                rows.reduce((acc, row) => {
                    if (row.rating != null) acc[row.id] = row.rating >= 3 ? "up" : "down";
                    return acc;
                }, {})
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /** Start a fresh conversation. */
    const startNewChat = useCallback(() => {
        const id = newSessionId();
        setSessionId(id);
        activeSessionRef.current = id;
        setMessages([]);
        setFeedbackState({});
    }, []);

    /** Delete a conversation session. */
    const deleteSession = useCallback(
        async (targetSessionId) => {
            const target = targetSessionId || sessionId;
            if (!target) return;

            const res = await remove(`${AI_CHAT}?session_id=${encodeURIComponent(target)}`, null);
            if (res?.status === "success" || res?.message?.includes("deleted")) {
                ToastMsg("success", "Conversation deleted.");
                setSessions((prev) => prev.filter((s) => s.session_id !== target));
                if (target === activeSessionRef.current) {
                    startNewChat();
                }
            } else {
                ToastMsg("error", res?.message || "Could not delete conversation.");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sessionId, startNewChat]
    );

    return {
        sessionId,
        messages,
        sending,
        sessions,
        loadingSessions,
        loadingHistory,
        feedbackState,
        send,
        rate,
        loadSessions,
        openSession,
        startNewChat,
        deleteSession,
    };
};

export default useCopilot;
